/**
 * Bar Replay Controller & State Machine.
 *
 * Manages bar-by-bar chronological playback, step indexing, speed throttling,
 * and slice generation for historical charts.
 */

export interface CandleLike {
  time: number | string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export interface VolumeLike {
  time: number | string;
  value: number;
  color?: string;
}

export interface StepResult<T, V> {
  done: boolean;
  candle?: T;
  volume?: V;
  index: number;
  total: number;
  stepCount: number;
}

export class ReplaySession<T = any, V = any> {
  public active: boolean = false;
  public playing: boolean = false;
  public fullCandles: T[] = [];
  public fullVolume: V[] = [];
  public currentIndex: number = 0;
  public startIndex: number = 0;
  public speed: number = 1;

  constructor() {}

  /**
   * Initializes or resets replay mode with full dataset snapshot.
   */
  public enter(candles: T[], volumeData: V[] = []): void {
    this.active = true;
    this.playing = false;
    this.fullCandles = Array.isArray(candles) ? candles.slice() : [];
    this.fullVolume = Array.isArray(volumeData) ? volumeData.slice() : [];
    this.currentIndex = this.fullCandles.length;
    this.startIndex = 0;
  }

  /**
   * Exits replay mode and returns full dataset to restore chart.
   */
  public exit(): { fullCandles: T[]; fullVolume: V[] } {
    this.active = false;
    this.playing = false;
    return {
      fullCandles: this.fullCandles,
      fullVolume: this.fullVolume,
    };
  }

  /**
   * Sets start point on timeline and slices dataset up to that index.
   */
  public setStart(index: number): { initialCandles: T[]; initialVolume: V[] } {
    if (!this.active) return { initialCandles: [], initialVolume: [] };
    const clampedIndex = Math.max(0, Math.min(index, this.fullCandles.length));
    this.startIndex = clampedIndex;
    this.currentIndex = clampedIndex;
    return {
      initialCandles: this.fullCandles.slice(0, clampedIndex),
      initialVolume: this.fullVolume.slice(0, clampedIndex),
    };
  }

  /**
   * Advances replay by one bar.
   */
  public step(): StepResult<T, V> {
    if (!this.active) {
      return { done: true, index: 0, total: 0, stepCount: 0 };
    }

    const total = this.fullCandles.length;
    if (this.currentIndex >= total) {
      this.playing = false;
      return { done: true, index: total, total, stepCount: total - this.startIndex };
    }

    const idx = this.currentIndex;
    const candle = this.fullCandles[idx];
    const volume = this.fullVolume[idx];
    this.currentIndex++;

    return {
      done: false,
      candle,
      volume,
      index: this.currentIndex,
      total,
      stepCount: this.currentIndex - this.startIndex,
    };
  }

  /**
   * Computes timer tick interval in milliseconds based on speed multiplier.
   */
  public getTimerIntervalMs(): number {
    const s = Math.max(1, this.speed || 1);
    return Math.max(100, Math.round(1000 / s));
  }

  /**
   * Formats status label text for the UI.
   */
  public getStatusText(): string {
    if (!this.active) return "";
    const totalSteps = this.fullCandles.length - this.startIndex;
    const currentStep = this.currentIndex - this.startIndex;
    if (this.currentIndex >= this.fullCandles.length) return "Replay finished";
    return `Step ${currentStep} / ${totalSteps}`;
  }
}
